import { IUser } from '@snackpass/snackpass-types';
import {
    createSlice,
    createSelector,
    createAsyncThunk,
    PayloadAction,
} from '@reduxjs/toolkit';
import { isEmpty, prop } from 'lodash/fp';
import { RootState } from '../store';
import { LoadingStatus } from '../types';
import api from '../../API';
import { FirebaseUser } from '../../Types';
import { setAuthHeader } from '../../API/axios';
import { getDeviceToken } from 'src/modules/Utils/helpers';
import { isProd } from 'src/modules/Lib/config';

interface UserState {
    user: IUser | null;
    exists: boolean;
    isSignedIn: boolean;
    status: LoadingStatus;
}

const initialState = {
    user: null,
    exists: false,
    isSignedIn: false,
    status: LoadingStatus.Idle,
} as UserState;

type UserFound = {
    exists: boolean;
};

export const checkUserExists = createAsyncThunk(
    'user/exists',
    async (deviceId: string | null): Promise<UserFound | null> => {
        const deviceToken = getDeviceToken();
        if (deviceToken) return { exists: true };

        if (!deviceId) return null;
        const result = await api.users.exists(`${deviceId}@snackpass.co`);
        !isProd && console.log('[checkUserExists]', { result });
        return result.data;
    }
);

export const fetchUser = createAsyncThunk(
    'user/fetchUser',
    async (
        {
            deviceToken,
            firebaseUser,
        }: { deviceToken?: string; firebaseUser: FirebaseUser },
        { dispatch }
    ): Promise<IUser | null> => {
        try {
            if (deviceToken) {
                const res = await api.devices.getThis();
                const { device, user } = res;
                dispatch(
                    setUser({
                        ...user,
                        // @ts-expect-error storeRoles omitted from snackpassPermissions definition
                        snackpassPermissions: { storeIds: [device.storeId] },
                    })
                );
                dispatch(setIsSignedIn(!isEmpty(device)));
                return user;
            }

            await setAuthHeader(firebaseUser);
            const res = await api.users.getMe();
            const user = prop('data.user', res);
            dispatch(setUser(user));
            dispatch(setIsSignedIn(!isEmpty(firebaseUser)));
            return user;
        } catch (err) {
            dispatch(setIsSignedIn(false));
            return null;
        }
    }
);

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUser(state: UserState, action: PayloadAction<IUser>) {
            state.user = action.payload;
            state.isSignedIn = true;
        },
        setIsSignedIn(state: UserState, action: PayloadAction<boolean>) {
            state.isSignedIn = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(checkUserExists.pending, (state: UserState) => {
                state.exists = false;
            })
            .addCase(
                checkUserExists.fulfilled,
                (state: UserState, action: PayloadAction<UserFound | null>) => {
                    state.exists = action.payload?.exists || false;
                }
            )
            .addCase(checkUserExists.rejected, (state: UserState) => {
                state.exists = false;
            })
            .addCase(fetchUser.pending, (state: UserState) => {
                state.status = LoadingStatus.Loading;
            })
            .addCase(
                fetchUser.fulfilled,
                (state: UserState, action: PayloadAction<IUser | null>) => {
                    state.user = action.payload;
                    state.status = LoadingStatus.Success;
                }
            )
            .addCase(fetchUser.rejected, (state: UserState) => {
                state.status = LoadingStatus.Failed;
            });
    },
});

export const getUserState = (state: RootState) => state.user;
export const getUser = createSelector([getUserState], prop('user'));
export const getUserExists = createSelector(
    [getUserState],
    user => user.exists
);
export const getUserIsSignedIn = createSelector(
    [getUserState],
    user => user.isSignedIn
);
export const getUserStoreIds = createSelector(
    [getUser],
    user => user?.snackpassPermissions.storeIds ?? []
);

export const { setUser, setIsSignedIn } = userSlice.actions;
export const userReducer = userSlice.reducer;
